'use client';

import Image from 'next/image';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import nearScheduleClockSvg from '@/assets/schedules/near-schedule-clock.svg?url';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Sheet, SheetClose, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { ScheduleType } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-type.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const TITLE = 'Horário próximo';
const DESCRIPTION = 'O horário que você escolheu está próximo. Você tem certeza que quer agendar?';

interface NearScheduleModalProps {
  open: boolean;
  partnerId: string;
  scheduleType: ScheduleType;
  onConfirm: () => void;
  onDismiss: () => void;
}

export function NearScheduleModal({
  open,
  partnerId,
  scheduleType,
  onConfirm,
  onDismiss,
}: NearScheduleModalProps) {
  const analytics = getAnalyticsPlugin();

  const trackProps = { partner_id: partnerId, schedule_type: scheduleType };

  const handleDismiss = () => {
    analytics.track(AnalyticsEnum.DISMISSED_NEAR_SCHEDULE_WARNING, trackProps);
    onDismiss();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleDismiss();
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        data-testid={DataTestIdEnum.NEAR_SCHEDULE_MODAL}
        side="centerFit"
        hasHeader
        title={TITLE}
        description={DESCRIPTION}
        className="p-0 md:bg-transparent border-none overflow-x-hidden"
      >
        <SheetHeader className="bg-white">
          <div className="flex justify-end p-6 pb-0">
            <SheetClose
              data-testid={DataTestIdEnum.NEAR_SCHEDULE_MODAL_CLOSE}
              asChild
              className="text-gray-500 cursor-pointer px-2 hover:bg-gray-50"
            >
              <button type="button" aria-label="Fechar">
                <Icon icon={faTimes} size="xl" className="text-[1.625rem] hover:text-gray-900" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="flex flex-col bg-white px-6 pb-6 pt-2">
          <Image
            src={nearScheduleClockSvg}
            alt=""
            width={121}
            height={114}
            draggable={false}
            className="self-center"
          />

          <Typography
            variant={TypographyVariant.MODAL_TITLE}
            className="mt-6"
            data-testid={DataTestIdEnum.NEAR_SCHEDULE_MODAL_TITLE}
          >
            {TITLE}
          </Typography>

          <Typography
            className="mt-3 text-gray-700"
            data-testid={DataTestIdEnum.NEAR_SCHEDULE_MODAL_DESCRIPTION}
          >
            {DESCRIPTION}
          </Typography>

          <div className="mt-7 flex flex-col gap-4">
            <Button
              data-testid={DataTestIdEnum.NEAR_SCHEDULE_MODAL_CONFIRM_BUTTON}
              onClick={onConfirm}
            >
              Sim, quero agendar
            </Button>

            <Button
              variant="secondary"
              data-testid={DataTestIdEnum.NEAR_SCHEDULE_MODAL_CANCEL_BUTTON}
              onClick={handleDismiss}
            >
              Não, quero escolher outro
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
